import React, { useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import NavBar from "@/components/NavBar";
import CampaignAnalytics from "@/components/CampaignAnalytics";
import Pagination from "@/components/ui/pagination";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import {
  BarChart3,
  Download,
  FileText,
  MessageSquare,
  TrendingUp,
  Users,
  Calendar,
  Share2,
  Eye,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Target,
  Sparkles,
  Filter,
  FileSpreadsheet,
  FileJson,
  Star,
  FileImage,
} from "lucide-react";
import {
  useCampaign,
  useCampaignComments,
  useWaitlistSubscriptions,
  useCampaignResponses,
  usePricingFeedbackAnalytics,
} from "@/hooks/useApi";
import { toast } from "@/hooks/use-toast";

// Analytics data types
interface SentimentData {
  name: string;
  value: number;
  color: string;
}

interface FeedbackItem {
  id: string;
  text: string;
  count: number;
  sentiment: "positive" | "neutral" | "negative";
}

interface PriceData {
  label: string;
  count: number;
}

interface RawFeedback {
  id: string;
  user: string;
  comment: string;
  sentiment: "positive" | "neutral" | "negative";
  timestamp: string;
}

// Export options
interface ExportOptions {
  format: "csv" | "json" | "excel";
  dataTypes: {
    responses: boolean;
    comments: boolean;
    pricing: boolean;
    analytics: boolean;
  };
  dateRange: "all" | "last7days" | "last30days" | "last90days";
}

// Time period filter options
interface TimeFilter {
  value: "today" | "week" | "month" | "all" | "custom";
  label: string;
  description: string;
}

// Share report options
interface ShareReportOptions {
  format: "jpg" | "pdf";
  includeAnalytics: boolean;
  includeRawFeedback: boolean;
  includeWaitlist: boolean;
}

const CampaignReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("analytics");
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [showShareReportModal, setShowShareReportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: "csv",
    dataTypes: {
      responses: true,
      comments: true,
      pricing: true,
      analytics: true,
    },
    dateRange: "all",
  });

  // Time filter state
  const [timeFilter, setTimeFilter] = useState<TimeFilter["value"]>("all");

  // Share report options
  const [shareReportOptions, setShareReportOptions] =
    useState<ShareReportOptions>({
      format: "jpg",
      includeAnalytics: true,
      includeRawFeedback: true,
      includeWaitlist: false,
    });

  // Ref for capturing the report content
  const reportRef = useRef<HTMLDivElement>(null);

  // Time filter options
  const timeFilterOptions: TimeFilter[] = [
    { value: "today", label: "Today", description: "Last 24 hours" },
    { value: "week", label: "This Week", description: "Last 7 days" },
    { value: "month", label: "This Month", description: "Last 30 days" },
    { value: "all", label: "All Time", description: "Since campaign creation" },
  ];

  // Fetch campaign data
  const { data: campaign, isLoading, error } = useCampaign(id!);

  // Fetch comments for raw feedback
  const { data: comments = [] } = useCampaignComments(id!);

  // Fetch responses for raw feedback
  const { data: responses = [] } = useCampaignResponses(id!);

  // Fetch waitlist subscriptions
  const { data: waitlistSubscriptions = [], isLoading: isLoadingWaitlist } =
    useWaitlistSubscriptions(id!);

  // Pagination state
  const [rawFeedbackPage, setRawFeedbackPage] = useState(1);
  const [waitlistPage, setWaitlistPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch pricing feedback analytics
  const { data: pricingAnalytics, isLoading: isLoadingPricingAnalytics } =
    usePricingFeedbackAnalytics(id!);

  // Pagination logic for raw feedback
  const rawFeedbackData = useMemo(() => {
    const allFeedback = [
      // Responses as feedback items
      ...responses.map((response) => ({
        id: response.id,
        type: "response" as const,
        user: response.user?.name || "Anonymous",
        content: response.answer,
        timestamp: response.createdAt,
        question:
          campaign?.questions.find((q) => q.id === response.questionId)?.text ||
          "Unknown Question",
        questionType:
          campaign?.questions.find((q) => q.id === response.questionId)?.type ||
          "TEXT",
      })),
      // Comments as feedback items
      ...comments.map((comment) => ({
        id: comment.id,
        type: "comment" as const,
        user: comment.user.name,
        content: comment.content,
        timestamp: comment.createdAt,
        question: "Community Comment",
        questionType: "COMMENT" as const,
      })),
    ];

    // Sort by timestamp (newest first)
    return allFeedback.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [responses, comments, campaign?.questions]);

  const paginatedRawFeedback = useMemo(() => {
    const startIndex = (rawFeedbackPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return rawFeedbackData.slice(startIndex, endIndex);
  }, [rawFeedbackData, rawFeedbackPage]);

  const rawFeedbackTotalPages = Math.ceil(
    rawFeedbackData.length / itemsPerPage
  );

  // Pagination logic for waitlist
  const paginatedWaitlist = useMemo(() => {
    const startIndex = (waitlistPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return waitlistSubscriptions.slice(startIndex, endIndex);
  }, [waitlistSubscriptions, waitlistPage]);

  const waitlistTotalPages = Math.ceil(
    waitlistSubscriptions.length / itemsPerPage
  );

  // Calculate analytics data from campaign and real backend data
  const analyticsData = useMemo(() => {
    if (!campaign) return null;

    // Use real pricing feedback sentiment instead of comment-based analysis
    const sentimentData: SentimentData[] = [];
    if (pricingAnalytics?.sentimentDistribution) {
      const totalFeedback = pricingAnalytics.totalFeedback;
      const sentimentMap = {
        LOVE: { name: "Love it!", color: "#22c55e", emoji: "😍" },
        LIKE: { name: "Like it", color: "#4ade80", emoji: "🙂" },
        NEUTRAL: { name: "Neutral", color: "#94a3b8", emoji: "😐" },
        EXPENSIVE: { name: "Too Expensive", color: "#f59e0b", emoji: "😕" },
        NOT_INTERESTED: {
          name: "Not Interested",
          color: "#ef4444",
          emoji: "👎",
        },
      };

      pricingAnalytics.sentimentDistribution.forEach((item) => {
        const sentiment = item.sentiment as keyof typeof sentimentMap;
        const config = sentimentMap[sentiment];
        if (config && totalFeedback > 0) {
          sentimentData.push({
            name: config.name,
            value: Math.round((item._count.sentiment / totalFeedback) * 100),
            color: config.color,
          });
        }
      });
    }

    // Generate feedback insights from comments and pricing feedback
    const feedbackItems: FeedbackItem[] = [];

    // Add pricing feedback insights
    if (pricingAnalytics?.sentimentDistribution) {
      const loveCount =
        pricingAnalytics.sentimentDistribution.find(
          (s) => s.sentiment === "LOVE"
        )?._count.sentiment || 0;
      const likeCount =
        pricingAnalytics.sentimentDistribution.find(
          (s) => s.sentiment === "LIKE"
        )?._count.sentiment || 0;
      const expensiveCount =
        pricingAnalytics.sentimentDistribution.find(
          (s) => s.sentiment === "EXPENSIVE"
        )?._count.sentiment || 0;

      if (loveCount > 0) {
        feedbackItems.push({
          id: "pricing-love",
          text: `${loveCount} users love the pricing`,
          count: loveCount,
          sentiment: "positive",
        });
      }

      if (likeCount > 0) {
        feedbackItems.push({
          id: "pricing-like",
          text: `${likeCount} users like the pricing`,
          count: likeCount,
          sentiment: "positive",
        });
      }

      if (expensiveCount > 0) {
        feedbackItems.push({
          id: "pricing-expensive",
          text: `${expensiveCount} users find it expensive`,
          count: expensiveCount,
          sentiment: "negative",
        });
      }
    }

    // Add comment insights
    comments.slice(0, 3).forEach((comment, index) => {
      feedbackItems.push({
        id: `comment-${comment.id}`,
        text:
          comment.content.length > 50
            ? comment.content.substring(0, 50) + "..."
            : comment.content,
        count: comment._count.likes + 1,
        sentiment: comment._count.likes > 0 ? "positive" : "neutral",
      });
    });

    // Real pricing data from backend
    const priceData: PriceData[] = [];
    if (pricingAnalytics?.pricingOptionPreferences && campaign.pricingOptions) {
      pricingAnalytics.pricingOptionPreferences.forEach((pref) => {
        if (pref.selectedPricingOptionId) {
          const pricingOption = campaign.pricingOptions.find(
            (option) => option.id === pref.selectedPricingOptionId
          );
          if (pricingOption) {
            priceData.push({
              label: pricingOption.price,
              count: pref._count.selectedPricingOptionId,
            });
          }
        }
      });
    }

    // Raw feedback from comments
    const rawFeedback: RawFeedback[] = comments.map((comment) => ({
      id: comment.id,
      user: comment.user.name,
      comment: comment.content,
      sentiment:
        comment._count.likes > 2
          ? "positive"
          : comment._count.likes > 0
          ? "neutral"
          : "negative",
      timestamp: comment.createdAt,
    }));

    // Response analytics by question type
    const responseAnalytics = {
      totalResponses: responses.length,
      responsesByQuestion:
        campaign.questions?.map((question) => {
          const questionResponses = responses.filter(
            (r) => r.questionId === question.id
          );
          return {
            questionId: question.id,
            questionText: question.text,
            questionType: question.type,
            responseCount: questionResponses.length,
            responses: questionResponses,
          };
        }) || [],
      completionRate: campaign.questions?.length
        ? Math.round((responses.length / campaign.questions.length) * 100)
        : 0,
    };

    return {
      sentimentData,
      feedbackItems,
      priceData,
      rawFeedback,
      pricingAnalytics,
      responseAnalytics,
    };
  }, [campaign, comments, pricingAnalytics]);

  // Calculate days active
  const daysActive = useMemo(() => {
    if (!campaign) return 0;
    const created = new Date(campaign.createdAt);
    const now = new Date();
    return Math.ceil(
      (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
    );
  }, [campaign]);

  // Handle export
  const handleExport = () => {
    if (!campaign || !analyticsData) return;

    // In a real app, this would call an API endpoint to generate the export
    const exportData = {
      campaign: campaign,
      analytics: analyticsData,
      exportOptions,
    };

    // Mock export functionality
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${campaign.title.replace(/\s+/g, "_")}_report.${
      exportOptions.format
    }`;
    link.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Export successful! 📊",
      description: `Your ${exportOptions.format.toUpperCase()} report has been downloaded.`,
    });
    setShowExportModal(false);
  };

  // Handle share report
  const handleShareReport = async () => {
    if (!reportRef.current || !campaign) return;

    try {
      if (shareReportOptions.format === "jpg") {
        // Export as JPG
        const dataUrl = await toPng(reportRef.current, {
          quality: 1.0,
          backgroundColor: "white",
          width: 1200,
          height: 1600,
        });

        const link = document.createElement("a");
        link.download = `${campaign.title.replace(/\s+/g, "_")}_report.jpg`;
        link.href = dataUrl;
        link.click();

        toast({
          title: "Report exported as JPG! 📸",
          description: "Your campaign report has been saved as an image.",
        });
      } else {
        // Export as PDF
        const dataUrl = await toPng(reportRef.current, {
          quality: 1.0,
          backgroundColor: "white",
          width: 1200,
          height: 1600,
        });

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const imgWidth = 190;
        const imgHeight = (imgWidth * 1600) / 1200;
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Add image to PDF
        pdf.addImage(dataUrl, "PNG", 10, 10, imgWidth, imgHeight);

        // Add campaign title and metadata
        pdf.setFontSize(16);
        pdf.text(campaign.title, pageWidth / 2, 5, { align: "center" });

        pdf.setFontSize(10);
        pdf.text(
          `Generated on ${new Date().toLocaleDateString()}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );

        pdf.save(`${campaign.title.replace(/\s+/g, "_")}_report.pdf`);

        toast({
          title: "Report exported as PDF! 📄",
          description: "Your campaign report has been saved as a PDF document.",
        });
      }

      setShowShareReportModal(false);
    } catch (error) {
      console.error("Error exporting report:", error);
      toast({
        title: "Export failed",
        description: "Could not export the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Filter data based on time period
  const getFilteredData = () => {
    if (!campaign || !analyticsData) return null;

    const now = new Date();
    let filterDate: Date;

    switch (timeFilter) {
      case "today":
        filterDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "week":
        filterDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "month":
        filterDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        return analyticsData; // All time
    }

    // Filter comments and responses based on date
    const filteredComments = comments.filter(
      (comment) => new Date(comment.createdAt) >= filterDate
    );
    const filteredResponses = responses.filter(
      (response) => new Date(response.createdAt) >= filterDate
    );

    // Recalculate analytics with filtered data
    // This is a simplified version - in a real app, you'd want more sophisticated filtering
    return {
      ...analyticsData,
      rawFeedback: analyticsData.rawFeedback.filter(
        (feedback) => new Date(feedback.timestamp) >= filterDate
      ),
    };
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <NavBar />
        <div className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-slate-600">Loading campaign analytics...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <NavBar />
        <div className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-4">
                Campaign Not Found
              </h1>
              <p className="text-slate-600 mb-6">
                The campaign you're looking for doesn't exist or you don't have
                access to it.
              </p>
              <Button onClick={() => navigate("/browse")}>
                Browse Campaigns
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <NavBar />

      <div className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate(`/campaign/${id}`)}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Campaign
            </Button>
          </motion.div>

          {/* Enhanced Campaign Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden bg-white rounded-2xl border border-slate-200/60 shadow-lg mb-8"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5" />
            <div className="relative p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-primary/10 rounded-xl">
                      <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="px-2 sm:px-3 py-1 bg-primary/10 text-primary text-xs sm:text-sm font-medium rounded-full">
                      {campaign.category || "General"}
                    </span>
                    <Badge
                      variant={
                        campaign.status === "ACTIVE" ? "default" : "secondary"
                      }
                      className="text-xs sm:text-sm"
                    >
                      {campaign.status}
                    </Badge>
                  </div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-slate-900">
                    {campaign.title}
                  </h1>
                  <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
                    Campaign Analytics & Insights
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                  <Button
                    variant="outline"
                    className="gap-2 text-xs sm:text-sm"
                    onClick={() => setShowShareReportModal(true)}
                  >
                    <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Share Report</span>
                    <span className="sm:hidden">Share</span>
                  </Button>
                  <Button
                    className="gap-2 bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                    onClick={() => setShowExportModal(true)}
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Export Data</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                </div>
              </div>

              {/* Time Filter */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mt-4 sm:mt-6 justify-end">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-600" />
                  <span className="text-xs sm:text-sm font-medium text-slate-700">
                    Time Period:
                  </span>
                </div>
                <Select
                  value={timeFilter}
                  onValueChange={(value: TimeFilter["value"]) =>
                    setTimeFilter(value)
                  }
                >
                  <SelectTrigger className="w-full sm:w-48 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col text-left">
                          <span className="font-medium text-xs sm:text-sm">
                            {option.label}
                          </span>
                          <span className="text-xs text-slate-500">
                            {option.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mt-6 sm:mt-8">
                {[
                  {
                    icon: MessageSquare,
                    label: "Responses",
                    value: campaign._count.responses,
                    color: "text-blue-600",
                    description: "Total feedback received",
                  },
                  {
                    icon: Eye,
                    label: "Comments",
                    value: campaign._count.comments,
                    color: "text-green-600",
                    description: "Community discussions",
                  },
                  {
                    icon: Calendar,
                    label: "Days Active",
                    value: daysActive,
                    color: "text-purple-600",
                    description: "Campaign duration",
                  },
                  {
                    icon: TrendingUp,
                    label: "Engagement",
                    value:
                      campaign._count.responses > 0
                        ? Math.round(
                            (campaign._count.comments /
                              campaign._count.responses) *
                              100
                          )
                        : 0,
                    color: "text-amber-600",
                    description: "Comment/response ratio",
                    suffix: "%",
                  },
                  {
                    icon: Users,
                    label: "Waitlist",
                    value: waitlistSubscriptions.length,
                    color: "text-indigo-600",
                    description: "Subscribers",
                  },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-slate-200/40"
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div
                        className={`p-1.5 sm:p-2 rounded-lg ${stat.color
                          .replace("text-", "bg-")
                          .replace("-600", "-100")}`}
                      >
                        <stat.icon
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.color}`}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">
                          {stat.value}
                          {stat.suffix || ""}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-600">
                          {stat.label}
                        </p>
                        <p className="text-xs text-slate-500 hidden sm:block">
                          {stat.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Enhanced Report Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            ref={reportRef}
          >
            <Tabs
              defaultValue="analytics"
              value={activeTab}
              onValueChange={setActiveTab}
              className="bg-white rounded-2xl border border-slate-200/60 shadow-lg overflow-hidden"
            >
              <TabsList className="w-full justify-start border-b border-slate-100 rounded-none bg-slate-50/50 p-0 h-auto overflow-x-auto">
                <TabsTrigger
                  value="analytics"
                  className="relative px-3 sm:px-4 lg:px-6 py-3 sm:py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs sm:text-sm lg:text-base font-medium transition-all whitespace-nowrap flex-shrink-0"
                >
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Analytics</span>
                  <span className="sm:hidden">Analytics</span>
                </TabsTrigger>
                <TabsTrigger
                  value="raw-feedback"
                  className="relative px-3 sm:px-4 lg:px-6 py-3 sm:py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs sm:text-sm lg:text-base font-medium transition-all whitespace-nowrap flex-shrink-0"
                >
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Raw Feedback</span>
                  <span className="sm:hidden">Feedback</span>
                </TabsTrigger>
                <TabsTrigger
                  value="waitlist"
                  className="relative px-3 sm:px-4 lg:px-6 py-3 sm:py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs sm:text-sm lg:text-base font-medium transition-all whitespace-nowrap flex-shrink-0"
                >
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Waitlist</span>
                  <span className="sm:hidden">Waitlist</span>
                </TabsTrigger>
                <TabsTrigger
                  value="export"
                  className="relative px-3 sm:px-4 lg:px-6 py-3 sm:py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs sm:text-sm lg:text-base font-medium transition-all whitespace-nowrap flex-shrink-0"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Export Data</span>
                  <span className="sm:hidden">Export</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="analytics"
                className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6"
              >
                {analyticsData ? (
                  <CampaignAnalytics
                    responseCount={campaign._count.responses}
                    sentimentData={
                      getFilteredData()?.sentimentData ||
                      analyticsData.sentimentData
                    }
                    feedbackItems={
                      getFilteredData()?.feedbackItems ||
                      analyticsData.feedbackItems
                    }
                    priceData={
                      getFilteredData()?.priceData || analyticsData.priceData
                    }
                    pricingAnalytics={analyticsData.pricingAnalytics}
                    responseAnalytics={analyticsData.responseAnalytics}
                  />
                ) : (
                  <div className="text-center py-16">
                    <BarChart3 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      No Analytics Data Available
                    </h3>
                    <p className="text-slate-600 max-w-md mx-auto">
                      Analytics will appear here once your campaign receives
                      feedback and comments.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="raw-feedback" className="p-4 sm:p-6 lg:p-8">
                {rawFeedbackData.length > 0 ? (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-900">
                        Raw Feedback & Responses
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs sm:text-sm">
                          {rawFeedbackData.length} total items
                        </Badge>
                        {responses.length > 0 && (
                          <Badge
                            variant="outline"
                            className="text-xs sm:text-sm"
                          >
                            {responses.length} responses
                          </Badge>
                        )}
                        {comments.length > 0 && (
                          <Badge
                            variant="outline"
                            className="text-xs sm:text-sm"
                          >
                            {comments.length} comments
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Paginated Feedback List */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">
                            All Feedback (Page {rawFeedbackPage} of{" "}
                            {rawFeedbackTotalPages})
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {paginatedRawFeedback.map((feedback) => (
                          <div
                            key={feedback.id}
                            className="p-4 sm:p-6 bg-slate-50 rounded-lg border border-slate-200"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                  <span className="text-xs sm:text-sm font-medium text-slate-600">
                                    {feedback.user.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm sm:text-base font-medium text-slate-900">
                                    {feedback.user}
                                  </p>
                                  <p className="text-xs sm:text-sm text-slate-500">
                                    {new Date(
                                      feedback.timestamp
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <Badge
                                variant={
                                  feedback.type === "comment"
                                    ? "secondary"
                                    : "outline"
                                }
                                className="text-xs sm:text-sm"
                              >
                                {feedback.type === "comment"
                                  ? "Comment"
                                  : feedback.questionType}
                              </Badge>
                            </div>

                            {feedback.type === "comment" ? (
                              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                                {feedback.content}
                              </p>
                            ) : (
                              <div>
                                <p className="text-xs sm:text-sm text-slate-600 mb-2">
                                  <strong>Question:</strong> {feedback.question}
                                </p>
                                <div className="mt-3">
                                  {feedback.questionType === "TEXT" && (
                                    <p className="text-slate-700 leading-relaxed">
                                      {feedback.content}
                                    </p>
                                  )}

                                  {feedback.questionType === "EMOJI" && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-2xl">
                                        {feedback.content}
                                      </span>
                                      <span className="text-sm text-slate-600">
                                        {feedback.content === "😍" &&
                                          "Love it!"}
                                        {feedback.content === "🙂" && "Like it"}
                                        {feedback.content === "😐" && "Neutral"}
                                        {feedback.content === "😕" && "Unsure"}
                                        {feedback.content === "👎" &&
                                          "Not for me"}
                                      </span>
                                    </div>
                                  )}

                                  {feedback.questionType === "MULTIPLE" && (
                                    <div className="flex items-center gap-2">
                                      <Badge
                                        variant="secondary"
                                        className="bg-primary-100 text-primary-700"
                                      >
                                        {feedback.content}
                                      </Badge>
                                    </div>
                                  )}

                                  {feedback.questionType === "RATING" && (
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-1">
                                        {Array.from(
                                          {
                                            length: parseInt(feedback.content),
                                          },
                                          (_, i) => (
                                            <Star
                                              key={i}
                                              className="w-4 h-4 fill-yellow-400 text-yellow-400"
                                            />
                                          )
                                        )}
                                      </div>
                                      <span className="text-sm text-slate-600">
                                        {feedback.content} out of 5
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </CardContent>
                      {rawFeedbackTotalPages > 1 && (
                        <div className="p-4 border-t border-slate-200">
                          <Pagination
                            currentPage={rawFeedbackPage}
                            totalPages={rawFeedbackTotalPages}
                            totalItems={rawFeedbackData.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={(page) => setRawFeedbackPage(page)}
                          />
                        </div>
                      )}
                    </Card>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <MessageSquare className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      No Feedback Yet
                    </h3>
                    <p className="text-slate-600 max-w-md mx-auto">
                      Raw feedback and responses will appear here once users
                      start engaging with your campaign.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="export" className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                      Export Campaign Data
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600">
                      Download your campaign data in various formats for further
                      analysis.
                    </p>
                  </div>

                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base">
                          Export Options
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <div className="space-y-3 sm:space-y-4">
                          <Label className="text-sm sm:text-base font-medium">
                            Export Format
                          </Label>
                          <Select
                            value={exportOptions.format}
                            onValueChange={(value: "csv" | "json" | "excel") =>
                              setExportOptions((prev) => ({
                                ...prev,
                                format: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="csv">
                                CSV (Spreadsheet)
                              </SelectItem>
                              <SelectItem value="json">
                                JSON (Developer)
                              </SelectItem>
                              <SelectItem value="excel">
                                Excel (Advanced)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-3 sm:space-y-4">
                          <Label className="text-sm sm:text-base font-medium">
                            Date Range
                          </Label>
                          <Select
                            value={exportOptions.dateRange}
                            onValueChange={(
                              value:
                                | "all"
                                | "last7days"
                                | "last30days"
                                | "last90days"
                            ) =>
                              setExportOptions((prev) => ({
                                ...prev,
                                dateRange: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Time</SelectItem>
                              <SelectItem value="last7days">
                                Last 7 Days
                              </SelectItem>
                              <SelectItem value="last30days">
                                Last 30 Days
                              </SelectItem>
                              <SelectItem value="last90days">
                                Last 90 Days
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3 sm:space-y-4">
                        <Label className="text-sm sm:text-base font-medium">
                          Data Types to Include
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                          {Object.entries(exportOptions.dataTypes).map(
                            ([key, value]) => (
                              <div
                                key={key}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  id={key}
                                  checked={value}
                                  onCheckedChange={(checked) =>
                                    setExportOptions((prev) => ({
                                      ...prev,
                                      dataTypes: {
                                        ...prev.dataTypes,
                                        [key]: checked as boolean,
                                      },
                                    }))
                                  }
                                />
                                <Label
                                  htmlFor={key}
                                  className="text-xs sm:text-sm font-medium capitalize"
                                >
                                  {key.replace(/([A-Z])/g, " $1").trim()}
                                </Label>
                              </div>
                            )
                          )}
                        </div>
                      </div>

                      <Button
                        onClick={() => setShowExportModal(true)}
                        className="w-full bg-primary hover:bg-primary/90 text-sm sm:text-base"
                        size="lg"
                      >
                        <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        Generate Export
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="waitlist" className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                        Waitlist Subscribers
                      </h3>
                      <p className="text-sm sm:text-base text-slate-600">
                        Manage your waitlist and communicate with subscribers.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                      <Badge variant="outline" className="text-xs sm:text-sm">
                        {waitlistSubscriptions.length} subscribers
                      </Badge>
                      <Button
                        onClick={() => setShowBulkMessageModal(true)}
                        className="bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                        disabled={waitlistSubscriptions.length === 0}
                      >
                        <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">
                          Send Bulk Message
                        </span>
                        <span className="sm:hidden">Bulk Message</span>
                      </Button>
                    </div>
                  </div>

                  {isLoadingWaitlist ? (
                    <div className="text-center py-16">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">Loading waitlist data...</p>
                    </div>
                  ) : waitlistSubscriptions.length > 0 ? (
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">
                            Subscriber List
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {paginatedWaitlist.map((subscription) => (
                            <div
                              key={subscription.id}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 rounded-lg border border-slate-200"
                            >
                              <div className="flex items-center gap-3 sm:gap-4">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                  <span className="text-xs sm:text-sm font-medium text-primary">
                                    {subscription.email.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm sm:text-base font-medium text-slate-900 truncate">
                                    {subscription.email}
                                  </p>
                                  <p className="text-xs sm:text-sm text-slate-500">
                                    Subscribed on{" "}
                                    {new Date(
                                      subscription.subscribedAt
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant={
                                    subscription.isActive
                                      ? "default"
                                      : "secondary"
                                  }
                                  className="text-xs sm:text-sm"
                                >
                                  {subscription.isActive
                                    ? "Active"
                                    : "Inactive"}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                      {waitlistTotalPages > 1 && (
                        <div className="p-4 border-t border-slate-200">
                          <Pagination
                            currentPage={waitlistPage}
                            totalPages={waitlistTotalPages}
                            totalItems={waitlistSubscriptions.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={(page) => setWaitlistPage(page)}
                          />
                        </div>
                      )}
                    </Card>
                  ) : (
                    <div className="text-center py-16">
                      <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        No Waitlist Subscribers Yet
                      </h3>
                      <p className="text-slate-600 max-w-md mx-auto">
                        When people subscribe to your waitlist, they'll appear
                        here. You can then send them updates and announcements.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Campaign Data
            </DialogTitle>
            <DialogDescription>
              Choose your export options and download your campaign data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select
                value={exportOptions.format}
                onValueChange={(value: "csv" | "json" | "excel") =>
                  setExportOptions((prev) => ({ ...prev, format: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      CSV (Spreadsheet)
                    </div>
                  </SelectItem>
                  <SelectItem value="json">
                    <div className="flex items-center gap-2">
                      <FileJson className="w-4 h-4" />
                      JSON (Developer)
                    </div>
                  </SelectItem>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel (Advanced)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Types</Label>
              <div className="space-y-2">
                {Object.entries(exportOptions.dataTypes).map(([key, value]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`modal-${key}`}
                      checked={value}
                      onCheckedChange={(checked) =>
                        setExportOptions((prev) => ({
                          ...prev,
                          dataTypes: {
                            ...prev.dataTypes,
                            [key]: checked as boolean,
                          },
                        }))
                      }
                    />
                    <Label
                      htmlFor={`modal-${key}`}
                      className="text-sm capitalize"
                    >
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              className="bg-primary hover:bg-primary/90"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Report Modal */}
      <Dialog
        open={showShareReportModal}
        onOpenChange={setShowShareReportModal}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Campaign Report
            </DialogTitle>
            <DialogDescription>
              Export your campaign report as an image or PDF to share with
              stakeholders.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Export Format</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      shareReportOptions.format === "jpg"
                        ? "border-primary bg-primary/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() =>
                      setShareReportOptions((prev) => ({
                        ...prev,
                        format: "jpg",
                      }))
                    }
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FileImage className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">JPG Image</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      High-quality image perfect for sharing on social media or
                      presentations
                    </p>
                  </div>
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      shareReportOptions.format === "pdf"
                        ? "border-primary bg-primary/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() =>
                      setShareReportOptions((prev) => ({
                        ...prev,
                        format: "pdf",
                      }))
                    }
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-red-600" />
                      <span className="font-medium">PDF Document</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Professional PDF document ideal for reports and
                      documentation
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Include Sections</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-analytics"
                      checked={shareReportOptions.includeAnalytics}
                      onCheckedChange={(checked) =>
                        setShareReportOptions((prev) => ({
                          ...prev,
                          includeAnalytics: checked as boolean,
                        }))
                      }
                    />
                    <Label htmlFor="include-analytics" className="text-sm">
                      Analytics Dashboard
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-raw-feedback"
                      checked={shareReportOptions.includeRawFeedback}
                      onCheckedChange={(checked) =>
                        setShareReportOptions((prev) => ({
                          ...prev,
                          includeRawFeedback: checked as boolean,
                        }))
                      }
                    />
                    <Label htmlFor="include-raw-feedback" className="text-sm">
                      Raw Feedback & Responses
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-waitlist"
                      checked={shareReportOptions.includeWaitlist}
                      onCheckedChange={(checked) =>
                        setShareReportOptions((prev) => ({
                          ...prev,
                          includeWaitlist: checked as boolean,
                        }))
                      }
                    />
                    <Label htmlFor="include-waitlist" className="text-sm">
                      Waitlist Subscribers
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Professional Report Export 📊
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Create a beautiful snapshot of your campaign performance
                    that you can share with:
                  </p>
                  <ul className="text-slate-600 text-sm mt-2 space-y-1">
                    <li>• Investors and stakeholders</li>
                    <li>• Team members and collaborators</li>
                    <li>• Social media and marketing materials</li>
                    <li>• Presentations and reports</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowShareReportModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleShareReport}
              className="bg-primary hover:bg-primary/90"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Message Modal */}
      <Dialog
        open={showBulkMessageModal}
        onOpenChange={setShowBulkMessageModal}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Send Bulk Message
            </DialogTitle>
            <DialogDescription>
              Send a message to all your waitlist subscribers. This feature is
              coming soon!
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="p-6 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Bulk Messaging Feature Coming Soon! 🚀
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    We're working on a powerful bulk messaging system that will
                    allow you to:
                  </p>
                  <ul className="text-slate-600 text-sm mt-2 space-y-1">
                    <li>
                      • Send personalized emails to your waitlist subscribers
                    </li>
                    <li>• Track email open rates and engagement</li>
                    <li>• Schedule messages for optimal timing</li>
                    <li>• Segment subscribers based on their interests</li>
                    <li>• A/B test different message variations</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-slate-500 mb-4">
                You have{" "}
                <strong>{waitlistSubscriptions.length} subscribers</strong>{" "}
                ready to receive your updates.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowBulkMessageModal(false)}
                className="w-full"
              >
                Got it, thanks!
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignReport;
